import {logger} from '@otto-ec/global-resources/debug';
import {insertHtmlFragment} from "@otto-ec/global-resources/dom";
import {trackAdjust} from "@otto-ec/apps-api/thirdpartytracking"

const log = logger('ft3.promo-aggregation');

export class Aggregation {
    private static readonly STOREFRONT_DRESON = '(und.(ja).(~.(v.1)))';

    private static readonly SCROLL_INTO_VIEW_DELAY_MILLIS: number = 250;
    private static readonly SCROLL_CALC_DELAY: number = 150;

    private static aggregationContainer: HTMLElement;

    init(): Promise<void> {
        Aggregation.aggregationContainer = document.getElementById('promo-aggregation')!;
        Aggregation.overrideScrollRestorationIfSupported();
        const widgetSelector = this.getWidgetSelectorForRestoration();
        if (widgetSelector !== null) {
            Aggregation.blurScreen();
        } else {
            Aggregation.unblurScreen();
        }
        Aggregation.initAppSettings();
        return Aggregation.loadAllTheModules(document.getElementsByClassName('promo-module-container')[0]).then(() =>
            Aggregation.restoreScrollPosition(widgetSelector),
        );
    }

    private static loadAllTheModules(container: Element): Promise<void[]> {
        const allModules = container.querySelectorAll('.promo-module-hidden[data-module-url]');
        const ssid = container.getAttribute('data-ssid')!;
        return Promise.all(Aggregation.fetchModules(allModules, ssid));
    }

    private static async fetchModule(module: Element, ssid: string): Promise<void> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 7500);

        try {
            const url: string = module.getAttribute('data-module-url')!;
            /*                                                                                                         */
            const headers = new Headers({'X-Origin-Ssid-Aggregation': ssid, 'X-Requested-With': 'XMLHttpRequest'});
            const response = await fetch(url, {
                headers: headers,
                method: 'GET',
                signal: controller.signal,
            });

            if (response.status !== 200) {
                log.debug(`promo-aggregation: Failed to fetch ${url}, status was ${response.status}`);
                /*                                    */
                throw new Error(`Failed to fetch module: ${url}`);
            }

            const html = await response.text();
            await insertHtmlFragment(html, module.parentElement!);

            clearTimeout(timeoutId);
        } catch (e) {
            if (module.parentNode) {
                module.parentNode.removeChild(module);
            }

            clearTimeout(timeoutId);
            log.debug('promo-aggregation: Failed to fetch widget', e);
        }
    }

    private static fetchModules(allModules: NodeListOf<Element>, ssid: string): Promise<void>[] {
        /*                                                                   */
        if (!window.AbortController || !window.fetch) {
            log.debug('AbortController or fetch not supported by browser');

            /*                                                                                              */
            return [Promise.resolve()];
        }

        return Array.from(allModules).map((module) => this.fetchModule(module, ssid));
    }

    private static overrideScrollRestorationIfSupported() {
        if ('scrollRestoration' in window.history) {
            log.info('scrollRestoration is', window.history.scrollRestoration);
            if (window.history.scrollRestoration !== 'manual') {
                window.history.scrollRestoration = 'manual';
                log.info('scrollRestoration has been set to', window.history.scrollRestoration);
            }
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    log.info('Page changes state to hidden.');
                    Aggregation.storeSelectorOrScrollPosition();
                }
            });
            window.addEventListener('scroll', () => Aggregation.handleScroll());
        }
    }

    private static storeSelectorOrScrollPosition() {
        const topWidgetSelector = Aggregation.findTopWidgetSelector();
        if (topWidgetSelector != null) {
            log.info('Storing widget selector for later back or forward navigation:', topWidgetSelector);
            const newState = Object.assign({}, window.history.state, {
                masLastWidgetSelector: topWidgetSelector,
                masLastScrollPosition: null,
            });
            try {
                window.history.replaceState(newState, '');
            } catch (e) {
                log.warn('replace state failed', e);
            }
            /*                                                                               */
            try {
                window.sessionStorage.setItem(
                    'promo-aggregation-back-forward-selector-' + Aggregation.hashCode(location.href),
                    topWidgetSelector,
                );
                /*                                                         */
            } catch (e) {
                /*                         */
            }
            log.info('promo-aggregation: window.history:', window.history);
        }
    }

    private static hashCode = (s: string) => s.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);

    private static findTopWidgetSelector(): string | null {
        /**
 *
 *
 */
        const fullScreenComboListElement = document.getElementById("fullscreenView")
        if (fullScreenComboListElement && fullScreenComboListElement.style.display !== "none") {
            return null;
        }

        const modules = document.querySelectorAll(
            '.reptile-tile-item, .promo-module-foreground, .gridContainer > header, .promo_static-promotion-teaser-item, #nav-consultant, .shoppagesFragment, .sp_textBlock'
        );
        for (let i = 0; i < modules.length; i++) {
            const module = modules.item(i) as HTMLElement;
            if (this.isElementInViewport(module)) {
                /*                                                                                             */
                /*                                                                                                */
                /*                                                                              */
                if (i + 1 < modules.length && module.contains(modules.item(i + 1))) {
                    continue;
                }
                /*                                                                                   */
                /*                                                 */
                if (Aggregation.isMoreThan40PercentOfElementVisible(module.getBoundingClientRect())) {
                    return Aggregation.createSelector(module as HTMLElement);
                } else {
                    while (i < modules.length) {
                        i++;
                        if (this.isElementInViewport(modules.item(i))) {
                            return Aggregation.createSelector(modules.item(i) as HTMLElement);
                        }
                    }
                    return Aggregation.createSelector(module as HTMLElement);
                }
            }
        }
        return null;
    }

    private static createSelector(elm: HTMLElement): string {
        /*                       */
        if (elm.id) {
            if (isNaN(parseInt(elm.id[0]))) {
                return `#${elm.id}`;
            } else {
                return `[id='${elm.id}']`;
            }
        }

        /*                       */
        if (elm.dataset?.variationId) {
            return `[data-variation-id="${elm.dataset.variationId}"]`;
        }

        let element: Element = elm;
        const path = [];
        let parent;
        while (element.parentNode) {
            parent = element.parentNode;
            /*         */
            const tag = element.tagName;
            let siblings: HTMLCollection;
            path.unshift(
                element.id
                    ? `#${element.id}`
                    : ((siblings = parent.children),
                        /*         */
                        [].filter.call(siblings, (sibling) => sibling.tagName === tag).length === 1
                            ? tag
                            : /*         */
                            `${tag}:nth-child(${1 + [].indexOf.call(siblings, element)})`),
            );
            /*         */
            element = parent;
        }
        return `${path.join(' > ')}`.toLowerCase();
    }

    private static isElementInViewport(el: Element) {
        if (!el) {
            return false;
        }
        const rect = el.getBoundingClientRect();
        return (
            rect.height > 0 &&
            (this.isElementEndingOrStartingInViewport(rect) || this.doesElementOccupyTheWholeScreen(rect))
        );
    }

    private static isMoreThan40PercentOfElementVisible(rect: DOMRect) {
        return rect.top + rect.height > rect.height * 0.4;
    }

    private static isElementEndingOrStartingInViewport(rect: DOMRect) {
        return (
            (rect.top >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight)) ||
            (rect.bottom >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight))
        );
    }

    private static doesElementOccupyTheWholeScreen(rect: DOMRect) {
        return rect.top < 0 && rect.bottom > (window.innerHeight || document.documentElement.clientHeight);
    }

    private static restoreScrollPosition(widgetSelector: string | null): Promise<void> {
        if (widgetSelector === null) {
            return Promise.resolve();
        }

        /*                                   */
        return new Promise(function (this: Aggregation, myResolve: () => void) {
            setTimeout(() => {
                const lastSeenWidgetElement = document.querySelector(widgetSelector);
                log.info('Scrolling to element with query selector:', widgetSelector);
                Aggregation.unblurScreen();
                lastSeenWidgetElement?.scrollIntoView();
                const cleanState = {...window.history.state};
                delete cleanState.masLastWidgetSelector;
                delete cleanState.masLastScrollPosition;
                try {
                    window.history.replaceState(cleanState, '');
                } catch (e) {
                    log.warn('replace state failed', e);
                }
                myResolve();
            }, Aggregation.SCROLL_INTO_VIEW_DELAY_MILLIS);
        });
    }

    private getWidgetSelectorForRestoration(): string | null {
        let backOrForward = false;
        let perfEntries: PerformanceEntry[] = [];
        try {
            perfEntries = performance.getEntriesByType('navigation');
            /*                                                         */
        } catch (e) {
            /*                                  */
        }
        /*         */
        if (perfEntries.length && perfEntries[0].type === 'back_forward') {
            log.info('Found back_forward in performance entries with type navigation.');
            backOrForward = true;
        }
        log.info('window.history:', window.history);

        let fallbackSelector: string | null = null;
        try {
            fallbackSelector = window.sessionStorage.getItem(
                'promo-aggregation-back-forward-selector-' + Aggregation.hashCode(location.href),
            );
            /*                                                         */
        } catch (e) {
            /*                        */
        }

        if (
            !backOrForward ||
            ((!window.history.state || !window.history.state.masLastWidgetSelector) && !fallbackSelector)
        ) {
            if (!window.history.state) {
                log.info('window.history.state is empty.');
            } else if (!window.history.state.masLastWidgetSelector) {
                log.info('window.history.state.masLastWidgetSelector is not set.');
            }

            if (!fallbackSelector) {
                log.info('no fallback selector in local storage.');
            }

            log.info(
                'Restore scroll position handler detected no backward or forward navigation or no saved state to scroll to.',
            );
            return null;
        }

        const widgetSelector =
            window.history.state && window.history.state.masLastWidgetSelector
                ? window.history.state.masLastWidgetSelector
                : fallbackSelector;
        if (!window.history.state || !window.history.state.masLastWidgetSelector) {
            log.info('Using fallback selector.');
        }
        return widgetSelector;
    }

    private static handleScroll() {
        const scrollY = window.scrollY;
        setTimeout(() => {
            if (scrollY === window.scrollY) {
                Aggregation.storeSelectorOrScrollPosition();
            }
        }, Aggregation.SCROLL_CALC_DELAY);
    }

    private static blurScreen(): void {
        Aggregation.aggregationContainer.classList.add('disabled-by-promo-aggregation');
    }

    private static unblurScreen(): void {
        Aggregation.aggregationContainer.classList.remove('disabled-by-promo-aggregation');
    }

    private static initAppSettings(): void {
        if (Aggregation.isApp()) {
            /*                                                                                                                                                                                     */
            document.body.style.backgroundColor = '#f0f0f0';

            if (Aggregation.aggregationContainer.getAttribute('data-dreson') === Aggregation.STOREFRONT_DRESON) {
                /*                                               */
                trackAdjust({
                        eventName: {
                            android: '3jiz37',
                            ios: 'td91zl',
                        },
                        partnerParameters: {
                            sitePath: 'Homepage',
                        }
                    }
                )
            }
        }
    }

    private static isApp(): boolean {
        const appCookie = Aggregation.getCookie('app');
        return appCookie === 'true';
    }

    /*                       */
    private static getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }

    private static decodeWidgetType(b64EncodedWidgetType: string | null, chunkSize: number = 3): string | null {
        let widgetType = '';
        if (b64EncodedWidgetType === null) {
            return null;
        }
        try {
            const enrichedWidgetType = atob(b64EncodedWidgetType); /*                                                                                                          */
            for (let i = 0; i < enrichedWidgetType.length; i += chunkSize + 1) {
                widgetType += enrichedWidgetType[i];
            }
            return widgetType;
            /*                                                         */
        } catch (e) {
            return b64EncodedWidgetType;
        }
    }
}
